import React, { useState } from 'react';
import './AdminList.css';

function AdminList({ ads = [], loading = false, error = null, onSelectAd, selectedAdId, onUpdateAd, onCreateAd, onGeneratePreviewVideo, onRemovePreviewVideo, generatingVideoFor, source, onEditAd }) {
  const isGoogleSheets = source === 'sheets';
  const [filter, setFilter] = useState('all'); // For Firebase: 'all', 'testing', 'live' | For Sheets: 'all', 'ad-created', 'ad-not-created'
  const [dateFilter, setDateFilter] = useState({ startDate: '', endDate: '' });
  const [searchTerm, setSearchTerm] = useState('');

  if (loading) {
    return (
      <div className="admin-list-loading">
        <div className="spinner"></div>
        <p>Loading ads...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-list-error">
        <p>❌ {error}</p>
      </div>
    );
  }

  // Helper function to parse date from timestamp
  const parseDate = (timestamp) => {
    if (!timestamp) return null;
    
    // Try to parse as Date object first
    if (timestamp instanceof Date) {
      return timestamp;
    }
    
    // Try parsing as ISO string or common date formats
    const date = new Date(timestamp);
    if (!isNaN(date.getTime())) {
      return date;
    }
    
    // Try parsing Google Sheets date format (e.g., "1/15/2024" or "15/1/2024")
    const parts = String(timestamp).split(/[-/]/);
    if (parts.length === 3) {
      // Assume format: MM/DD/YYYY or DD/MM/YYYY
      const year = parseInt(parts[2] || parts[0]);
      const month = parseInt(parts[0] || parts[1]) - 1;
      const day = parseInt(parts[1] || parts[0]);
      const parsed = new Date(year, month, day);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    
    return null;
  };

  // Filter ads based on selected filter and date range
  const filteredAds = ads.filter((ad) => {
    // Apply status filter
    let passesStatusFilter = true;
    if (isGoogleSheets) {
      // Google Sheets filters: 'all', 'ad-created', 'ad-not-created'
      if (filter === 'ad-created') {
        passesStatusFilter = ad.adCreated && ad.adCreated.trim() !== '';
      } else if (filter === 'ad-not-created') {
        passesStatusFilter = !ad.adCreated || ad.adCreated.trim() === '';
      }
    } else {
      // Firebase filters: 'all', 'testing', 'live'
      if (filter === 'testing') {
        passesStatusFilter = ad.debug === true;
      } else if (filter === 'live') {
        passesStatusFilter = ad.enabled === true || ad.enabled === undefined || ad.enabled === null;
      }
    }

    if (!passesStatusFilter) return false;

    // Apply text search
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (normalizedSearch) {
      const text = [
        ad.business_name,
        ad.name,
        ad.title,
        ad.description,
        ad.business_description,
        ad.id,
        ad.adCreated
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (!text.includes(normalizedSearch)) {
        return false;
      }
    }

    // Apply date filter (only for Google Sheets ads with timestamp)
    if (isGoogleSheets && (dateFilter.startDate || dateFilter.endDate)) {
      const adDate = parseDate(ad.timestamp);
      if (!adDate) return false; // Exclude ads without valid timestamp

      const startDate = dateFilter.startDate ? new Date(dateFilter.startDate) : null;
      const endDate = dateFilter.endDate ? new Date(dateFilter.endDate) : null;

      // Set time to start/end of day for proper comparison
      if (startDate) {
        startDate.setHours(0, 0, 0, 0);
      }
      if (endDate) {
        endDate.setHours(23, 59, 59, 999);
      }

      const adDateOnly = new Date(adDate);
      adDateOnly.setHours(0, 0, 0, 0);

      if (startDate && adDateOnly < startDate) return false;
      if (endDate && adDateOnly > endDate) return false;
    }

    return true;
  });

  if (ads.length === 0) {
    return (
      <div className="admin-list-empty">
        <p>No ads found.</p>
      </div>
    );
  }

  return (
    <div className="admin-list">
      <div className="admin-list-header">
        <h2>Ads List</h2>
        <span className="ads-count">{filteredAds.length} ads</span>
      </div>
      <div className="admin-list-filters">
        <form
          className="admin-search-row"
          onSubmit={(e) => {
            e.preventDefault();
            setSearchTerm(searchTerm.trim());
          }}
        >
          <input
            type="text"
            className="admin-search-input"
            placeholder="חיפוש לפי שם עסק או מזהה פרסומת"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              className="admin-search-clear"
              onClick={() => setSearchTerm('')}
              aria-label="נקה חיפוש"
            >
              ✕
            </button>
          )}
          <button type="submit" className="admin-search-button">
            חיפוש
          </button>
        </form>
        <div className="filter-buttons-group">
          <button
            className={`filter-button ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          {isGoogleSheets ? (
            <>
              <button
                className={`filter-button ${filter === 'ad-created' ? 'active' : ''}`}
                onClick={() => setFilter('ad-created')}
              >
                Ad Created
              </button>
              <button
                className={`filter-button ${filter === 'ad-not-created' ? 'active' : ''}`}
                onClick={() => setFilter('ad-not-created')}
              >
                Ad Not Created
              </button>
            </>
          ) : (
            <>
              <button
                className={`filter-button ${filter === 'testing' ? 'active' : ''}`}
                onClick={() => setFilter('testing')}
              >
                Testing
              </button>
              <button
                className={`filter-button ${filter === 'live' ? 'active' : ''}`}
                onClick={() => setFilter('live')}
              >
                Live
              </button>
            </>
          )}
        </div>
        {isGoogleSheets && (
          <div className="date-filter-group">
            <input
              type="date"
              className="date-filter-input"
              value={dateFilter.startDate}
              onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
              placeholder="Start Date"
            />
            <span className="date-filter-separator">to</span>
            <input
              type="date"
              className="date-filter-input"
              value={dateFilter.endDate}
              onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
              placeholder="End Date"
            />
            {(dateFilter.startDate || dateFilter.endDate) && (
              <button
                className="date-filter-clear"
                onClick={() => setDateFilter({ startDate: '', endDate: '' })}
                title="Clear date filter"
              >
                ✕
              </button>
            )}
          </div>
        )}
      </div>
      {filteredAds.length === 0 ? (
        <div className="admin-list-empty">
          <p>No ads found for the selected filter.</p>
        </div>
      ) : (
        <div className="admin-list-items">
          {filteredAds.map((ad) => {
            const isFirebaseAd = source === 'firebase' || ad.source === 'firebase';
            
            const handleToggleClick = async (field, currentValue, e) => {
              e.stopPropagation(); // Prevent ad selection when clicking toggle
              
              // Prevent the checkbox from changing immediately
              e.preventDefault();
              
              const newValue = !currentValue;
              
              if (window.confirm('Do you want to update the database?')) {
                if (onUpdateAd && ad.id) {
                  await onUpdateAd(ad.id, field, newValue);
                  // The checkbox will update automatically when Firebase data refreshes
                }
              }
              // If user cancels, the checkbox state remains unchanged
            };

            return (
              <div
                key={ad.id}
                className={`admin-list-item ${selectedAdId === ad.id ? 'selected' : ''}`}
                onClick={() => onSelectAd(ad)}
              >
                <div className="admin-list-item-content">
                  <h3 className="admin-list-item-title">
                    {isFirebaseAd 
                      ? (ad.description || ad.title || ad.name || `Ad ${ad.id}`)
                      : (ad.title || ad.name || `Ad ${ad.id}`)
                    }
                  </h3>
                  <div className="admin-list-item-meta">
                    {ad.id && <span className="ad-id">ID: {ad.id}</span>}
                    {isGoogleSheets && ad.adCreated && ad.adCreated.trim() !== '' && (
                      <span className="ad-id">Ad ID: {ad.adCreated}</span>
                    )}
                    {ad.source && <span className="ad-source">{ad.source === 'google-sheets' ? '📊 Sheets' : '🔥 Firebase'}</span>}
                    {ad.timestamp && (
                      <span className="ad-date" title={ad.timestamp}>
                        📅 {(() => {
                          const date = parseDate(ad.timestamp);
                          return date ? date.toLocaleDateString() : ad.timestamp;
                        })()}
                      </span>
                    )}
                    {!ad.timestamp && ad.createdAt && (
                      <span className="ad-date">
                        {new Date(ad.createdAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  {ad.description && (
                    <p className="admin-list-item-description">
                      {ad.description.length > 100
                        ? `${ad.description.substring(0, 100)}...`
                        : ad.description}
                    </p>
                  )}
                  {isFirebaseAd && (
                    <div className="admin-list-item-toggles">
                      <label className="toggle-label" onClick={(e) => e.stopPropagation()}>
                        <span>Testing</span>
                        <input
                          type="checkbox"
                          className="toggle-switch"
                          checked={ad.debug === true}
                          onClick={(e) => handleToggleClick('debug', ad.debug === true, e)}
                          onChange={(e) => e.preventDefault()}
                        />
                      </label>
                      <label className="toggle-label" onClick={(e) => e.stopPropagation()}>
                        <span>Live</span>
                        <input
                          type="checkbox"
                          className="toggle-switch"
                          checked={ad.enabled === true || ad.enabled === undefined || ad.enabled === null}
                          onClick={(e) => {
                            const currentValue = ad.enabled === true || ad.enabled === undefined || ad.enabled === null;
                            handleToggleClick('enabled', currentValue, e);
                          }}
                          onChange={(e) => e.preventDefault()}
                        />
                      </label>
                      <label className="toggle-label" onClick={(e) => e.stopPropagation()}>
                        <span>Portrait</span>
                        <input
                          type="checkbox"
                          className="toggle-switch"
                          checked={ad.portrait === true}
                          onClick={(e) => handleToggleClick('portrait', ad.portrait === true, e)}
                          onChange={(e) => e.preventDefault()}
                        />
                      </label>
                    </div>
                  )}
                  {(() => {
                    // Check if ad has both image and video
                    const hasImage = ad.media && (
                      (Array.isArray(ad.media) && ad.media.length > 0 && ad.media[0]?.image) ||
                      (typeof ad.media === 'object' && Object.keys(ad.media).length > 0 && 
                       Object.values(ad.media)[0]?.image)
                    );
                    const hasVideo = !!(ad.video || ad.vimeo || ad.youtubeID);
                    const canCreateAd = hasImage && hasVideo;
                    const canGenerateVideo = hasImage && hasVideo;
                    const hasPreviewVideo = !!(ad.previewVideo);
                    
                    // For Google Sheets, only show "צור פרסומת" button if ad not created
                    // But always show video preview buttons for both Firebase and Google Sheets
                    const showCreateAdButton = isGoogleSheets 
                      ? (!ad.adCreated || ad.adCreated.trim() === '')
                      : false; // Firebase ads don't have this button
                    
                    let disabledTitle = '';
                    if (!hasImage && !hasVideo) {
                      disabledTitle = 'חסרים תמונה ווידאו';
                    } else if (!hasImage) {
                      disabledTitle = 'חסרה תמונה';
                    } else if (!hasVideo) {
                      disabledTitle = 'חסר וידאו';
                    }
                    
                    return (
                      <div className="admin-list-item-actions">
                        {showCreateAdButton && (
                          <button
                            className="create-ad-button"
                            disabled={!canCreateAd}
                            title={disabledTitle}
                            onClick={canCreateAd ? async (e) => {
                              e.stopPropagation();
                              if (window.confirm('האם אתה בטוח שברצונך ליצור פרסומת בפיירבייס במצב test?')) {
                                if (onCreateAd) {
                                  await onCreateAd(ad);
                                }
                              }
                            } : undefined}
                          >
                            צור פרסומת
                          </button>
                        )}
                        {isFirebaseAd && onEditAd && (
                          <button
                            className="edit-ad-button"
                            title="ערוך פרסומת"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditAd(ad);
                            }}
                          >
                            ערוך פרסומת
                          </button>
                        )}
                        <div className="video-buttons-container">
                          {hasPreviewVideo ? (
                            <>
                              <button
                                className="download-preview-video-button"
                                disabled={false}
                                title="הורד וידאו מקדים"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(ad.previewVideo, '_blank');
                                }}
                              >
                                הורד וידאו מקדים
                              </button>
                              <button
                                className={
                                  generatingVideoFor === ad.id 
                                    ? "creating-preview-video-button" 
                                    : "rebuild-preview-video-button"
                                }
                                disabled={!canGenerateVideo || generatingVideoFor === ad.id}
                                title={
                                  generatingVideoFor === ad.id 
                                    ? 'יוצרים וידאו מקדים...' 
                                    : disabledTitle || 'בנה מחדש וידאו'
                                }
                                onClick={canGenerateVideo && generatingVideoFor !== ad.id ? (e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  
                                  // Set state immediately to show loading animation
                                  // This will be handled by the parent component, but we trigger it here
                                  // The state is managed in AdminPage, so we just call the function
                                  // which will set the state immediately
                                  (async () => {
                                    try {
                                      if (onGeneratePreviewVideo) {
                                        await onGeneratePreviewVideo(ad, source);
                                      }
                                    } catch (error) {
                                      // Error handling is done in handleGeneratePreviewVideo
                                    }
                                  })();
                                } : undefined}
                              >
                                {generatingVideoFor === ad.id ? (
                                  <span className="creating-video-text">
                                    <span className="spinner-small"></span>
                                    יוצרים וידאו מקדים...
                                  </span>
                                ) : (
                                  'בנה מחדש וידאו'
                                )}
                              </button>
                              <button
                                className="remove-preview-video-button"
                                disabled={false}
                                title="הסר וידאו מקדים"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onRemovePreviewVideo) {
                                    onRemovePreviewVideo(ad, source);
                                  }
                                }}
                              >
                                הסר וידאו מקדים
                              </button>
                            </>
                          ) : (
                            <button
                              className={
                                generatingVideoFor === ad.id 
                                  ? "creating-preview-video-button" 
                                  : "create-preview-video-button"
                              }
                              disabled={!canGenerateVideo || generatingVideoFor === ad.id}
                              title={
                                generatingVideoFor === ad.id 
                                  ? 'יוצרים וידאו מקדים...' 
                                  : disabledTitle || 'צור וידאו מקדים'
                              }
                              onClick={canGenerateVideo && generatingVideoFor !== ad.id ? (e) => {
                                e.stopPropagation();
                                
                                // Call the async function - state will be set in handleGeneratePreviewVideo
                                (async () => {
                                  try {
                                    if (onGeneratePreviewVideo) {
                                      await onGeneratePreviewVideo(ad, source);
                                    }
                                  } catch (error) {
                                    // Error handling is done in handleGeneratePreviewVideo
                                  }
                                })();
                              } : undefined}
                            >
                              {generatingVideoFor === ad.id ? (
                                <span className="creating-video-text">
                                  <span className="spinner-small"></span>
                                  יוצרים וידאו מקדים...
                                </span>
                              ) : (
                                'צור וידאו מקדים'
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AdminList;
